import React, { useState } from "react";
import { 
  Search, MapPin, Sparkles, ShieldCheck, CheckCircle2, ArrowRight, 
  Building2, Users, Briefcase, TrendingUp, HelpCircle, ChevronRight, 
  Check, X, Eye, Phone, Globe, Layers, AlertCircle, Info, Bookmark, ExternalLink
} from "lucide-react";
import { AudienceModeId } from "../../config/audienceModes";
import { resolveLocationSlug } from "../../utils/locationRoutes";
import { resolveCanonicalSearchIntent } from "../../utils/canonicalIntentResolver";

/* -------------------------------------------------------------------------- */
/* TYPES & INTERFACES                                                         */
/* -------------------------------------------------------------------------- */

export interface SampleBusinessCard {
  name: string;
  category: string;
  location: string;
  phone?: string;
  website?: string;
  signalTier: "high" | "possible" | "unconfirmed";
  signalBadgeText: string;
  reason: string;
  freshness: string;
  verifiability: string;
}

export interface FaqItem {
  question: string;
  answer: string;
}

export interface InternalLinkGroup {
  title: string;
  links: { label: string; path: string }[];
}

export interface AudiencePainPointCard {
  title: string;
  roleId: AudienceModeId;
  painPoint: string;
  benefit: string;
  exampleSignal: string;
  path: string;
}

/* -------------------------------------------------------------------------- */
/* 1. HERO SECTION                                                            */
/* -------------------------------------------------------------------------- */

export function SeoHeroSection({
  eyebrowText = "Uncover Unadvertised Local Opportunities",
  title,
  subtitle,
  primaryCtaText,
  primaryCtaPath,
  secondaryCtaText,
  secondaryCtaPath,
  locationName,
  onNavigate,
  onLogin
}: {
  eyebrowText?: string;
  title: string | React.ReactNode;
  subtitle: string;
  primaryCtaText: string;
  primaryCtaPath: string;
  secondaryCtaText?: string;
  secondaryCtaPath?: string;
  locationName?: string;
  onNavigate: (path: string) => void;
  onLogin?: (isSignUp?: boolean) => void;
}) {
  return (
    <section className="bg-white dark:bg-slate-900 rounded-3xl p-6 sm:p-10 border border-slate-200 dark:border-slate-800 shadow-sm relative overflow-hidden space-y-6">
      {/* Background Accent Grid */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:20px_20px] pointer-events-none" />

      <div className="relative z-10 max-w-4xl space-y-4">
        {/* Eyebrow */}
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-50 dark:bg-blue-950/80 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 text-xs font-black uppercase tracking-wider">
          <Sparkles className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
          <span>{eyebrowText}</span>
          {locationName && (
            <span className="bg-blue-200/60 dark:bg-blue-800/60 text-blue-900 dark:text-blue-100 px-2 py-0.5 rounded text-[10px] font-bold">
              {locationName}
            </span>
          )}
        </div>

        {/* Title */}
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 dark:text-white tracking-tight leading-[1.15]">
          {title}
        </h1>

        {/* Subtitle */}
        <p className="text-sm sm:text-base text-slate-600 dark:text-slate-300 leading-relaxed font-normal max-w-3xl">
          {subtitle}
        </p>

        {/* CTAs */}
        <div className="flex flex-wrap items-center gap-3 pt-2">
          <button
            onClick={() => onNavigate(primaryCtaPath)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-extrabold px-6 py-3 rounded-xl shadow-md transition cursor-pointer text-xs sm:text-sm flex items-center gap-2"
          >
            <span>{primaryCtaText}</span>
            <ArrowRight className="w-4 h-4" />
          </button>

          {secondaryCtaText && secondaryCtaPath && (
            <button
              onClick={() => {
                if (secondaryCtaPath === "/login") {
                  onLogin?.(true);
                } else {
                  onNavigate(secondaryCtaPath);
                }
              }}
              className="bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 font-bold px-5 py-3 rounded-xl border border-slate-200 dark:border-slate-700 transition cursor-pointer text-xs sm:text-sm flex items-center gap-2"
            >
              <span>{secondaryCtaText}</span>
            </button>
          )}
        </div>

        {/* Quick Highlights Row */}
        <div className="flex flex-wrap items-center gap-4 pt-4 border-t border-slate-100 dark:border-slate-800 text-xs font-extrabold text-slate-600 dark:text-slate-300">
          <div className="flex items-center gap-1.5">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            <span>Direct Owner Outreach</span>
          </div>
          <div className="flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            <span>0 Phantom Job Ads</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Info className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            <span>Evidence-Based Signals</span>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* 2. SEARCH & ACTION MODULE                                                  */
/* -------------------------------------------------------------------------- */

export function SeoSearchActionModule({
  defaultLocation = "",
  defaultKeyword = "",
  onNavigate,
  onHomepageSearch
}: {
  defaultLocation?: string;
  defaultKeyword?: string;
  onNavigate: (path: string) => void;
  onHomepageSearch?: (query: string, location: string) => void;
}) {
  const [keyword, setKeyword] = useState(defaultKeyword);
  const [location, setLocation] = useState(defaultLocation);
  const [selectedRole, setSelectedRole] = useState<AudienceModeId>("job_seeker");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const resolved = resolveCanonicalSearchIntent(keyword, location);
    if (resolved && resolved.isStructuredIntent) {
      onNavigate(resolved.canonicalPath);
      return;
    }
    if (onHomepageSearch) {
      onHomepageSearch(keyword, location);
    } else {
      onNavigate(`/find-jobs?q=${encodeURIComponent(keyword)}&loc=${encodeURIComponent(location)}`);
    }
  };

  const suggestPills = [
    { name: "Medical & Healthcare", slug: "medical-healthcare" },
    { name: "Dental Clinics", slug: "dental-clinics" },
    { name: "Plumbing & Trades", slug: "plumbing-trades" },
    { name: "Legal & Accounting", slug: "legal-accounting" },
    { name: "Tech Agencies", slug: "tech-agencies" },
    { name: "Retail & Hospitality", slug: "retail-hospitality" }
  ];

  return (
    <section className="bg-white dark:bg-slate-900 text-slate-900 dark:text-white rounded-3xl p-6 sm:p-8 border border-slate-200 dark:border-slate-800 shadow-lg space-y-6">
      <div className="space-y-2">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 text-[11px] font-black uppercase">
          <Search className="w-3.5 h-3.5" />
          <span>Interactive Business Search</span>
        </div>
        <h2 className="text-xl sm:text-2xl font-black tracking-tight">
          Search local businesses showing activity signals
        </h2>
        <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300">
          Filter operating local businesses by industry or location to review public contact methods and business signals.
        </p>
      </div>

      {/* Role / Mode Filter */}
      <div className="space-y-2">
        <span className="text-[11px] font-black uppercase text-slate-500 dark:text-slate-400 tracking-wider block">
          Your Purpose / Role Mode:
        </span>
        <div className="flex flex-wrap gap-2">
          {[
            { id: "job_seeker", label: "Job Seeker" },
            { id: "recruiter", label: "Recruiter" },
            { id: "sales_professional", label: "Sales & B2B" },
            { id: "agency_freelancer", label: "Agency & Freelancer" },
            { id: "business_development", label: "Local Explorer" }
          ].map(r => (
            <button
              key={r.id}
              onClick={() => setSelectedRole(r.id as AudienceModeId)}
              className={`px-3 py-1.5 rounded-lg text-xs font-extrabold transition cursor-pointer border ${
                selectedRole === r.id
                  ? "bg-blue-600 text-white border-blue-600 shadow-xs"
                  : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-200 dark:hover:bg-slate-700"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Search Input Form */}
      <form onSubmit={handleSubmit} className="bg-slate-50 dark:bg-slate-800 p-2 rounded-2xl border border-slate-200 dark:border-slate-700 flex flex-col sm:flex-row gap-2">
        <div className="flex-1 flex items-center gap-2 bg-white dark:bg-slate-900 px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700">
          <Search className="w-4 h-4 text-slate-400 shrink-0" />
          <input
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="Business category, skill, or role..."
            className="w-full bg-transparent text-xs sm:text-sm font-semibold text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none"
          />
        </div>

        <div className="flex-1 flex items-center gap-2 bg-white dark:bg-slate-900 px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700">
          <MapPin className="w-4 h-4 text-slate-400 shrink-0" />
          <input
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="City or ZIP code..."
            className="w-full bg-transparent text-xs sm:text-sm font-semibold text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none"
          />
        </div>

        <button
          type="submit"
          className="bg-blue-600 hover:bg-blue-700 text-white font-extrabold px-6 py-3 rounded-xl transition cursor-pointer text-xs sm:text-sm flex items-center justify-center gap-2 shrink-0 shadow-md"
        >
          <span>Search Businesses</span>
          <ArrowRight className="w-4 h-4" />
        </button>
      </form>

      {/* Suggested Searches / Category Chips */}
      <div className="flex flex-wrap items-center gap-2 pt-1 text-xs">
        <span className="text-slate-500 dark:text-slate-400 font-bold text-[11px] uppercase tracking-wider shrink-0">
          Suggested Searches:
        </span>
        {suggestPills.map((pill) => {
          const resolvedLoc = location ? resolveLocationSlug(location) : null;
          const targetLocSlug = resolvedLoc && resolvedLoc.type !== "invalid" && resolvedLoc.type !== "reserved" 
            ? resolvedLoc.normalizedSlug 
            : "";
          
          const targetUrl = targetLocSlug
            ? `/jobs/${targetLocSlug}/${pill.slug}`
            : `/jobs/${pill.slug}`;

          return (
            <a
              key={pill.slug}
              href={targetUrl}
              onClick={(e) => {
                e.preventDefault();
                onNavigate(targetUrl);
              }}
              className="bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 border border-slate-200/90 dark:border-slate-700 hover:bg-blue-50/80 dark:hover:bg-slate-700 hover:border-blue-300 dark:hover:border-slate-600 hover:text-blue-700 dark:hover:text-blue-300 rounded-xl px-3 py-1.5 text-[11px] font-bold transition cursor-pointer flex items-center gap-1.5 shadow-2xs group"
            >
              <span className="text-blue-600 dark:text-blue-400 font-extrabold group-hover:scale-110 transition-transform">+</span>
              <span>{pill.name}</span>
            </a>
          );
        })}
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* 3. WHY THIS PAGE EXISTS SECTION                                            */
/* -------------------------------------------------------------------------- */

export function SeoWhyPageExistsSection({
  locationName = "Your Area",
  contextDescription
}: {
  locationName?: string;
  contextDescription?: string;
}) {
  return (
    <section className="bg-white dark:bg-slate-900 rounded-3xl p-6 sm:p-8 border border-slate-200 dark:border-slate-800 shadow-sm space-y-6">
      <div className="space-y-2">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 dark:bg-emerald-950/80 border border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-300 text-xs font-black uppercase">
          <Check className="w-3.5 h-3.5 text-emerald-600" />
          <span>Why This Page Exists</span>
        </div>
        <h2 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight">
          How BizNearYou differs from passive job portals in {locationName}
        </h2>
        {contextDescription && (
          <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 leading-relaxed font-normal">
            {contextDescription}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Traditional Job Boards */}
        <div className="bg-rose-50/40 dark:bg-rose-950/20 border border-rose-200 dark:border-rose-900/40 rounded-2xl p-5 space-y-3">
          <div className="flex items-center gap-2 text-rose-700 dark:text-rose-400 font-extrabold text-sm">
            <X className="w-4 h-4" />
            <span>National Job Boards & Portals</span>
          </div>
          <ul className="space-y-2.5 text-xs text-slate-700 dark:text-slate-300 font-medium">
            <li className="flex items-start gap-2">
              <span className="text-rose-600 font-black">✗</span>
              <span><strong>Exorbitant ad costs ($300-$1,000/mo)</strong> price out local clinics and trade shops.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-rose-600 font-black">✗</span>
              <span><strong>500+ bot applicant floods</strong> crowd out local community job seekers.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-rose-600 font-black">✗</span>
              <span><strong>Automated ATS keyword bots</strong> silently discard qualified local resumes.</span>
            </li>
          </ul>
        </div>

        {/* BizNearYou Direct Sourcing */}
        <div className="bg-emerald-50/40 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/40 rounded-2xl p-5 space-y-3">
          <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400 font-extrabold text-sm">
            <Check className="w-4 h-4" />
            <span>BizNearYou Direct Signal Sourcing</span>
          </div>
          <ul className="space-y-2.5 text-xs text-slate-700 dark:text-slate-300 font-medium">
            <li className="flex items-start gap-2">
              <span className="text-emerald-600 font-black">✓</span>
              <span><strong>Direct local business contact</strong> connects with employers without middleman job forms.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-600 font-black">✓</span>
              <span><strong>Discover local small businesses</strong> that hire through direct contact.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-600 font-black">✓</span>
              <span><strong>Conservative evidence badges</strong> ensure verifiability and transparency.</span>
            </li>
          </ul>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* 4. EXAMPLE RESULTS PREVIEW (SAMPLE BUSINESS CARDS)                         */
/* -------------------------------------------------------------------------- */

export function SeoExampleResultsPreview({
  cards,
  locationName = "",
  onNavigate,
  onLogin
}: {
  cards: SampleBusinessCard[];
  locationName?: string;
  onNavigate: (path: string) => void;
  onLogin?: (isSignUp?: boolean) => void;
}) {
  return (
    <section className="bg-white dark:bg-slate-900 rounded-3xl p-6 sm:p-8 border border-slate-200 dark:border-slate-800 shadow-sm space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="space-y-1">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-950/80 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 text-xs font-black uppercase">
            <Eye className="w-3.5 h-3.5" />
            <span>Live Result Preview</span>
          </div>
          <h2 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight">
            Sample business opportunities {locationName ? `in ${locationName}` : "indexed"}
          </h2>
        </div>

        <button
          onClick={() => onNavigate("/find-jobs")}
          className="text-xs font-extrabold text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 cursor-pointer"
        >
          <span>View All Indexed Businesses</span>
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((card, idx) => {
          const badgeStyle = 
            card.signalTier === "high"
              ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30"
              : card.signalTier === "possible"
              ? "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/30"
              : "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30";

          return (
            <div
              key={idx}
              className="bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 space-y-3.5 flex flex-col justify-between hover:border-blue-500/60 transition shadow-2xs"
            >
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/80 border border-blue-200 dark:border-blue-900/60 px-2.5 py-0.5 rounded-full transition-colors">
                    {card.category}
                  </span>
                  <span className={`px-2.5 py-0.5 rounded text-[10px] font-black border ${badgeStyle}`}>
                    {card.signalBadgeText}
                  </span>
                </div>

                <h3 className="font-black text-slate-900 dark:text-white text-base leading-snug">
                  {card.name}
                </h3>

                <p className="text-[11px] text-slate-500 dark:text-slate-400 font-medium flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5 shrink-0" />
                  <span>{card.location}</span>
                </p>

                <div className="p-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs space-y-1">
                  <p className="font-extrabold text-slate-800 dark:text-slate-200 text-[11px] flex items-center gap-1">
                    <Sparkles className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                    Observed Growth Evidence:
                  </p>
                  <p className="text-[11px] text-slate-600 dark:text-slate-300 leading-normal font-medium">
                    {card.reason}
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="pt-2 border-t border-slate-200/60 dark:border-slate-800/60 flex items-center justify-between gap-2 text-xs">
                <span className="text-[10px] text-slate-400 font-medium">
                  {card.freshness}
                </span>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onNavigate("/how-signals-work")}
                    className="p-1.5 rounded-lg text-slate-500 hover:text-slate-900 dark:hover:text-white bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 cursor-pointer"
                    title="View Evidence Methodology"
                  >
                    <Info className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => onLogin?.(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-extrabold text-xs px-3 py-1.5 rounded-lg transition cursor-pointer flex items-center gap-1 shadow-2xs"
                  >
                    <span>Contact</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* 5. AUDIENCE BREAKDOWN SECTION                                              */
/* -------------------------------------------------------------------------- */

export function SeoAudienceBreakdown({
  cards,
  onNavigate
}: {
  cards: AudiencePainPointCard[];
  onNavigate: (path: string) => void;
}) {
  return (
    <section className="bg-white dark:bg-slate-900 rounded-3xl p-6 sm:p-8 border border-slate-200 dark:border-slate-800 shadow-sm space-y-6">
      <div className="space-y-1">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-50 dark:bg-indigo-950/80 border border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300 text-xs font-black uppercase">
          <Users className="w-3.5 h-3.5" />
          <span>Multi-Audience Workflows</span>
        </div>
        <h2 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight">
          How different professionals leverage this data
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((c, i) => (
          <div
            key={i}
            className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/80 rounded-2xl p-5 space-y-3 flex flex-col justify-between"
          >
            <div className="space-y-2">
              <span className="text-[10px] font-bold uppercase tracking-wider text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/80 border border-blue-200 dark:border-blue-900/60 px-2.5 py-0.5 rounded-full transition-colors">
                {c.title}
              </span>

              <p className="text-[11px] font-bold text-rose-600 dark:text-rose-400">
                Challenge: {c.painPoint}
              </p>

              <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed font-medium">
                {c.benefit}
              </p>

              <div className="p-2 bg-white dark:bg-slate-900 rounded-lg text-[10px] text-slate-600 dark:text-slate-400 font-mono border border-slate-200 dark:border-slate-800">
                <span className="font-bold text-slate-900 dark:text-white">Signal: </span>
                {c.exampleSignal}
              </div>
            </div>

            <button
              onClick={() => onNavigate(c.path)}
              className="text-xs font-extrabold text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 cursor-pointer pt-2"
            >
              <span>Explore Role Workflow</span>
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* 6. TRUST & SIGNAL EXPLANATION                                              */
/* -------------------------------------------------------------------------- */

export function SeoTrustSignalBlock({
  onNavigate
}: {
  onNavigate: (path: string) => void;
}) {
  return (
    <section className="bg-slate-50 dark:bg-slate-950 rounded-3xl p-6 border border-slate-200 dark:border-slate-800 space-y-3">
      <div className="flex items-center gap-2 text-blue-700 dark:text-blue-400 font-black text-sm">
        <ShieldCheck className="w-5 h-5 shrink-0" />
        <span>Evidence-Based Transparency Standard</span>
      </div>
      <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed font-medium">
        BizNearYou maintains a conservative evidence standard. Signals reflect verified public growth data (such as active careers pages, review surges, or new locations) and are updated regularly. We never guarantee employment; we empower you with direct owner contact details.
      </p>
      <div className="pt-1">
        <button
          onClick={() => onNavigate("/how-signals-work")}
          className="text-xs font-extrabold text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 cursor-pointer"
        >
          <span>Read complete signal scoring methodology</span>
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* 7. FAQ ACCORDION                                                           */
/* -------------------------------------------------------------------------- */

export function SeoFaqAccordion({
  faqs,
  title = "Frequently Asked Questions"
}: {
  faqs: FaqItem[];
  title?: string;
}) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section className="bg-white dark:bg-slate-900 rounded-3xl p-6 sm:p-8 border border-slate-200 dark:border-slate-800 shadow-sm space-y-6">
      <div className="space-y-1">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-200 text-xs font-black uppercase">
          <HelpCircle className="w-3.5 h-3.5 text-blue-600" />
          <span>Help & Clarity</span>
        </div>
        <h2 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight">
          {title}
        </h2>
      </div>

      <div className="space-y-3">
        {faqs.map((faq, idx) => {
          const isOpen = openIndex === idx;
          return (
            <div
              key={idx}
              className="border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden bg-slate-50 dark:bg-slate-800/40 transition"
            >
              <button
                onClick={() => setOpenIndex(isOpen ? null : idx)}
                className="w-full p-4.5 text-left font-extrabold text-xs sm:text-sm text-slate-900 dark:text-white flex items-center justify-between cursor-pointer hover:text-blue-600 transition"
              >
                <span>{faq.question}</span>
                <ChevronRight className={`w-4 h-4 transition-transform duration-200 shrink-0 ${isOpen ? "rotate-90 text-blue-600" : "text-slate-400"}`} />
              </button>
              {isOpen && (
                <div className="p-4.5 pt-0 text-xs text-slate-600 dark:text-slate-300 leading-relaxed font-medium border-t border-slate-200/60 dark:border-slate-700/60">
                  {faq.answer}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* 8. INTERNAL LINKING GRID                                                   */
/* -------------------------------------------------------------------------- */

export function SeoRelatedLinksGrid({
  groups,
  onNavigate
}: {
  groups: InternalLinkGroup[];
  onNavigate: (path: string) => void;
}) {
  return (
    <section className="bg-white dark:bg-slate-900 rounded-3xl p-6 sm:p-8 border border-slate-200 dark:border-slate-800 shadow-sm space-y-6">
      <div className="space-y-1">
        <h2 className="text-lg font-black text-slate-900 dark:text-white tracking-tight">
          Explore Related Searches & Nearby Guides
        </h2>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Cross-link related local directories, industry hubs, and audience role pages:
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {groups.map((group, idx) => (
          <div key={idx} className="space-y-2.5">
            <h3 className="text-xs font-black uppercase tracking-wider text-slate-800 dark:text-slate-200 border-b border-slate-100 dark:border-slate-800 pb-1.5">
              {group.title}
            </h3>
            <ul className="space-y-1.5">
              {group.links.map((link, lIdx) => (
                <li key={lIdx}>
                  <button
                    onClick={() => onNavigate(link.path)}
                    className="text-xs font-semibold text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition cursor-pointer flex items-center justify-between w-full py-1 group"
                  >
                    <span>{link.label}</span>
                    <ChevronRight className="w-3.5 h-3.5 text-slate-300 group-hover:translate-x-1 transition-transform" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* 9. STRONG CTA FOOTER BLOCK                                                 */
/* -------------------------------------------------------------------------- */

export function SeoCtaFooterBanner({
  title = "Start searching local businesses today",
  subtitle = "Free accounts include daily business searches, hiring signal audits, and pitch generation.",
  buttonText = "Start Free Search",
  buttonPath = "/find-jobs",
  onNavigate
}: {
  title?: string;
  subtitle?: string;
  buttonText?: string;
  buttonPath?: string;
  onNavigate: (path: string) => void;
}) {
  return (
    <section className="bg-white dark:bg-slate-900 text-slate-900 dark:text-white rounded-3xl p-8 sm:p-10 shadow-sm text-center space-y-5 border border-slate-200 dark:border-slate-800">
      <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-slate-900 dark:text-white">
        {title}
      </h2>
      <p className="text-slate-600 dark:text-slate-300 text-xs sm:text-sm max-w-2xl mx-auto leading-relaxed">
        {subtitle}
      </p>
      <div className="flex flex-wrap justify-center gap-3 pt-2">
        <button
          onClick={() => onNavigate(buttonPath)}
          className="bg-blue-600 hover:bg-blue-700 text-white font-extrabold px-6 py-3 rounded-xl shadow-xs transition cursor-pointer text-xs sm:text-sm flex items-center gap-2"
        >
          <span>{buttonText}</span>
          <ArrowRight className="w-4 h-4" />
        </button>
        <button
          onClick={() => onNavigate("/pricing")}
          className="bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-800 dark:text-white border border-slate-300 dark:border-slate-700 font-extrabold px-6 py-3 rounded-xl shadow-xs transition cursor-pointer text-xs sm:text-sm"
        >
          View Subscription Plans
        </button>
      </div>
    </section>
  );
}
